import clsx from 'clsx';
import { AnimatePresence } from 'motion/react';
import { lazy } from 'react';
import { Outlet } from 'react-router';

import styles from './mobile-layout.module.css';

import { ContextMenuController } from '/@/renderer/features/context-menu/context-menu-controller';
import { DemoBanner } from '/@/renderer/features/invite/components/demo-banner';
import { FullScreenVisualizer } from '/@/renderer/features/player/components/full-screen-visualizer';
import { MobileFullscreenPlayer } from '/@/renderer/features/player/components/mobile-fullscreen-player';
import { MobileSidebar } from '/@/renderer/features/sidebar/components/mobile-sidebar';
import { MobileSyncPlaceholder } from '/@/renderer/features/sync/components/mobile-sync-placeholder';
import { ModeToggle } from '/@/renderer/features/sync/components/mode-toggle';
import { PlayerBar } from '/@/renderer/layouts/default-layout/player-bar';
import { useAppMode } from '/@/renderer/store';
import { useFullScreenPlayerStore } from '/@/renderer/store';
import { useWindowSettings } from '/@/renderer/store';
import { ActionIcon } from '/@/shared/components/action-icon/action-icon';
import { Drawer } from '/@/shared/components/drawer/drawer';
import { useDisclosure } from '/@/shared/hooks/use-disclosure';
import { Platform } from '/@/shared/types/types';

const WindowBar = lazy(() =>
    import('/@/renderer/layouts/window-bar').then((module) => ({
        default: module.WindowBar,
    })),
);

interface MobileLayoutProps {
    shell?: boolean;
}

export const MobileLayout = ({ shell }: MobileLayoutProps) => {
    const [sidebarOpened, { close: closeSidebar, open: openSidebar }] = useDisclosure(false);
    const {
        expanded: isFullScreenPlayerExpanded,
        visualizerExpanded: isFullScreenVisualizerExpanded,
    } = useFullScreenPlayerStore();
    const { windowBarStyle } = useWindowSettings();
    const appMode = useAppMode();

    return (
        <>
            <div
                className={clsx(styles.layout, {
                    [styles.macos]: windowBarStyle === Platform.MACOS,
                    [styles.windows]: windowBarStyle === Platform.WINDOWS,
                })}
                id="mobile-layout"
            >
                {!shell && <WindowBar />}
                <ActionIcon
                    className={styles.drawerButton}
                    icon="menu"
                    onClick={openSidebar}
                    size="lg"
                    tooltip={{ label: 'Menu' }}
                    variant="subtle"
                />
                <main className={styles.mainContent}>
                    <ModeToggle withAppMenu={false} />
                    {/* Sticks to the top of the scroll container — see demo-banner.module.css. */}
                    <DemoBanner />
                    {appMode === 'sync' ? <MobileSyncPlaceholder /> : <Outlet />}
                </main>
                <PlayerBar />
            </div>
            <Drawer
                onClose={closeSidebar}
                opened={sidebarOpened}
                position="left"
                size="320px"
                styles={{
                    body: {
                        height: '100%',
                        padding: 0,
                    },
                    content: {
                        height: '100%',
                        width: '100%',
                    },
                }}
                withCloseButton={false}
            >
                <MobileSidebar />
            </Drawer>
            <AnimatePresence initial={false}>
                {isFullScreenPlayerExpanded && (
                    <div className={styles.fullScreenPlayerOverlay}>
                        <MobileFullscreenPlayer />
                    </div>
                )}
            </AnimatePresence>
            <AnimatePresence initial={false}>
                {isFullScreenVisualizerExpanded && (
                    <div className={styles.fullScreenPlayerOverlay}>
                        <FullScreenVisualizer />
                    </div>
                )}
            </AnimatePresence>
            <ContextMenuController.Root />
        </>
    );
};
